import React from 'react';
import { Button, Segmented, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatMoneyPl } from '../../../utils/currency';
import {
  commercialAllocationMethodLabelKey,
  DEFAULT_ALLOCATION_METHOD,
  formatAllocationKeyReasons,
  TRANSPORT_COST_ALLOCATION_METHOD_ORDER,
  type AllocationKeyAvailability,
  type AllocationMethod,
} from '../../../utils/transportCostAllocation';

const { Text } = Typography;

interface Props {
  totalPLN: number | null;
  savedKey: AllocationMethod;
  shownKey: AllocationMethod;
  /** Name of the default key on this truck (defaultKeyLabel). */
  defaultLabel: string;
  availability?: AllocationKeyAvailability;
  /** „Wartość towaru” needs the ZD line values; false while they are not loaded. */
  valuesReady: boolean;
  canSave: boolean;
  saving: boolean;
  onPreview: (key: AllocationMethod) => void;
  onSave: () => void;
}

/**
 * Above the trailer (owner 2026-10-06): how the truck's freight is split and by
 * which key; another key previews the split on the trailer and in the table,
 * „Zapisz klucz” saves it through the ordinary truck save (server split).
 */
export const TransportFreightKeyBar: React.FC<Props> = ({
  totalPLN, savedKey, shownKey, defaultLabel, availability, valuesReady, canSave, saving, onPreview, onSave,
}) => {
  const { t } = useTranslation(['transport']);
  const label = (key: AllocationMethod) => (key === DEFAULT_ALLOCATION_METHOD
    ? defaultLabel
    : t(`transport:${commercialAllocationMethodLabelKey(key)}`));
  const previewing = shownKey !== savedKey;

  // An inbound truck splits over ZD lines: „ręcznie” is for customer orders only.
  const options = TRANSPORT_COST_ALLOCATION_METHOD_ORDER.filter((key) => key !== 'MANUAL').map((key) => {
    const state = availability?.[key];
    const unavailable = key !== DEFAULT_ALLOCATION_METHOD && state ? !state.available : false;
    const waiting = key === 'BY_VALUE' && key !== savedKey && !valuesReady;
    const reason = unavailable
      ? t('transport:allocationKey.unavailableReason', { key: label(key), reasons: formatAllocationKeyReasons(state?.reasons) })
      : waiting ? t('transport:freightKey.valuesLoading') : null;
    return {
      value: key,
      disabled: (unavailable || waiting) && key !== savedKey,
      label: (
        <Tooltip title={reason}>
          <span data-testid={`transport-freight-key-${key}`}>
            {label(key)}{key === savedKey ? ' ✓' : ''}
          </span>
        </Tooltip>
      ),
    };
  });

  return (
    <div className="transport-freight-key" data-testid="transport-freight-key">
      <div className="transport-freight-key__row">
        <Text strong>
          {t('transport:freightKey.title', { amount: totalPLN === null ? '—' : formatMoneyPl(totalPLN, 'PLN') })}
        </Text>
        <Segmented size="small" value={shownKey} options={options} onChange={(value) => onPreview(value as AllocationMethod)} />
      </div>
      {previewing ? (
        <div className="transport-freight-key__preview" data-testid="transport-freight-key-preview">
          <Text type="warning">{t('transport:freightKey.previewNote', { key: label(savedKey) })}</Text>
          <span className="transport-freight-key__actions">
            <Button size="small" onClick={() => onPreview(savedKey)}>{t('transport:freightKey.reset')}</Button>
            {canSave ? (
              <Button size="small" type="primary" loading={saving} onClick={onSave} data-testid="transport-freight-key-save">
                {t('transport:freightKey.save', { key: label(shownKey) })}
              </Button>
            ) : null}
          </span>
        </div>
      ) : null}
    </div>
  );
};
